/**
 * Distance Change Page (距離編集).
 * Displays visit list with editable distance, Google Maps with draggable pins.
 * Opened from VisitScheduleDetailModal's 距離編集 button.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AppButton,
  AppCard,
  AppRadio,
  AppSpin,
  AppTable,
  AppTextField,
  AppTypography,
} from '@/components/atoms';
import { useDistanceChangeData, useDistanceSaveMutation } from '@/hooks/main/useVisitSchedule';
import { useAuth } from '@/shared/providers/AuthContext';
import type { DistanceChangeData, DistanceChangeRow } from '@/services/main/visitSchedule';
import type { ColumnsType } from 'antd/es/table';
import { VISIT_DISTANCE_THRESHOLD_KM } from '@/constants/visit';
import { calcDistanceGRS80 } from '@/utils/geoDistance';

interface EditableRow extends DistanceChangeRow {
  _distance: string;
  _is_non_charge: boolean;
  _is_non_visit_treatment: boolean;
  _is_from_massager: boolean;
  _group_number: string;
  _latitude: number | null;
  _longitude: number | null;
  /**
   * True when user changed something that affects distance for this row in
   * this session: typed in distance input or dragged the pin (drag also
   * cascades to following rows whose automatic 拠点 uses 前訪問先). Sent to
   * BE so it can persist the user-supplied distance to distance_caches.
   * Mirrors A2's edit_manual[] form field.
   */
  _edit_manual: boolean;
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

function hasCoordValue(v: unknown): boolean {
  return v !== null && v !== undefined && v !== '';
}

/** 訪問先 (to) — editable pin coords with API fallback */
function resolveToPoint(row: EditableRow): { lat: number; lng: number } | null {
  const tLat = hasCoordValue(row._latitude) ? Number(row._latitude) : Number(row.to_latitude);
  const tLng = hasCoordValue(row._longitude) ? Number(row._longitude) : Number(row.to_longitude);
  if (Number.isNaN(tLat) || Number.isNaN(tLng)) return null;
  if (tLat === 0 && tLng === 0) return null;
  return { lat: tLat, lng: tLng };
}

/**
 * 距離の起点: row0 は常に施術者; row1+ は施術者 or 前訪問先の短い方.
 */
function resolveDistanceFromPoint(
  rowIndex: number,
  row: EditableRow,
  rows: EditableRow[],
  massager: DistanceChangeData['massager'] | undefined
): { lat: number; lng: number } | null {
  if (!massager) return null;
  if (rowIndex === 0 || row._is_from_massager) {
    const mLat = Number(massager.latitude);
    const mLng = Number(massager.longitude);
    if (!Number.isNaN(mLat) && !Number.isNaN(mLng) && mLat !== 0 && mLng !== 0) {
      return { lat: mLat, lng: mLng };
    }
    return null;
  }
  const prev = rows[rowIndex - 1];
  if (!prev) return null;
  // 起点 = 前行の訪問先 (to) — same rules as resolveToPoint(prev)
  return resolveToPoint(prev);
}

function resolveMassagerPoint(
  massager: DistanceChangeData['massager'] | undefined
): { lat: number; lng: number } | null {
  if (!massager) return null;
  const mLat = Number(massager.latitude);
  const mLng = Number(massager.longitude);
  if (Number.isNaN(mLat) || Number.isNaN(mLng) || (mLat === 0 && mLng === 0)) return null;
  return { lat: mLat, lng: mLng };
}

function shouldUseMassagerStart(
  rowIndex: number,
  row: EditableRow,
  rows: EditableRow[],
  massager: DistanceChangeData['massager'] | undefined
): boolean {
  if (rowIndex === 0) return true;

  const toPt = resolveToPoint(row);
  const massagerPt = resolveMassagerPoint(massager);
  const previousPt = resolveToPoint(rows[rowIndex - 1]);

  if (!toPt) return row._is_from_massager;
  if (!massagerPt && !previousPt) return row._is_from_massager;
  if (massagerPt && !previousPt) return true;
  if (!massagerPt && previousPt) return false;

  const massagerDistance = calcDistanceGRS80(massagerPt!.lat, massagerPt!.lng, toPt.lat, toPt.lng);
  const previousDistance = calcDistanceGRS80(previousPt!.lat, previousPt!.lng, toPt.lat, toPt.lng);
  return massagerDistance <= previousDistance;
}

function getDistanceNumber(distance: unknown): number | null {
  if (distance == null || distance === '') return null;
  const value = Number(distance);
  return Number.isFinite(value) ? value : null;
}

function isAutoMeasureIncorrect(row: { distance: unknown }): boolean {
  const originalDistance = getDistanceNumber(row.distance);
  return originalDistance !== null && originalDistance < 0;
}

function canRecalculateDistance(row: EditableRow): boolean {
  return !isAutoMeasureIncorrect(row) || row._edit_manual;
}

/** Recompute automatic 拠点 choice + from_* + 距離 from startIndex onward. */
function recalculateDistancesFromRowIndex(
  draft: EditableRow[],
  startIndex: number,
  massager: DistanceChangeData['massager'] | undefined
): EditableRow[] {
  const next = [...draft];
  for (let i = startIndex; i < next.length; i += 1) {
    const r = next[i];
    if (!canRecalculateDistance(r)) {
      next[i] = { ...r, _distance: '-1' };
      continue;
    }
    const useMassager = shouldUseMassagerStart(i, r, next, massager);
    const basisRow = { ...r, _is_from_massager: useMassager };
    const fromPt = resolveDistanceFromPoint(i, basisRow, next, massager);
    const toPt = resolveToPoint(basisRow);
    let newDist = r._distance;
    if (fromPt && toPt) {
      newDist = String(calcDistanceGRS80(fromPt.lat, fromPt.lng, toPt.lat, toPt.lng));
    }
    next[i] = {
      ...basisRow,
      from_latitude: fromPt?.lat ?? null,
      from_longitude: fromPt?.lng ?? null,
      _distance: newDist,
    };
  }
  return next;
}

function joinDisplayParts(parts: Array<string | number | null | undefined>): string {
  return parts
    .map(part => (part == null ? '' : String(part).trim()))
    .filter(Boolean)
    .join(' ');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatPatientLabel(row: EditableRow | undefined): string {
  if (!row) return '';
  return joinDisplayParts([row.serial_number, row.patient_name || row.to_name]);
}

function formatVisitDestinationAddressLabel(row: EditableRow): string {
  return row.to_full_address?.trim() || joinDisplayParts([row.to_address, row.to_address3]);
}

function formatStartAddressLabel(
  row: EditableRow,
  rowIndex: number,
  rows: EditableRow[],
  massager: DistanceChangeData['massager'] | undefined
): string {
  if (rowIndex === 0 || row._is_from_massager) {
    return joinDisplayParts([row.from_address || massager?.address, row.location_name]);
  }
  const prev = rows[rowIndex - 1];
  return prev ? formatVisitDestinationAddressLabel(prev) : '';
}

function formatStartNameLabel(
  row: EditableRow,
  rowIndex: number,
  rows: EditableRow[],
  massager: DistanceChangeData['massager'] | undefined
): string {
  if (rowIndex === 0 || row._is_from_massager) {
    return massager?.name || row.from_name || '';
  }
  return formatPatientLabel(rows[rowIndex - 1]);
}

export default function DistanceChangePage() {
  const { visitdate, massagerId, clinicLocationId } = useParams<{
    visitdate: string;
    massagerId: string;
    clinicLocationId: string;
  }>();
  const { hideTenantLogoutButton } = useAuth();
  const isAdminClinicLogin = hideTenantLogoutButton;
  const mid = massagerId ? Number(massagerId) : null;
  const parsedClinicLocationId = clinicLocationId ? Number(clinicLocationId) : null;
  const cid =
    parsedClinicLocationId && Number.isFinite(parsedClinicLocationId)
      ? parsedClinicLocationId
      : null;
  const { data, isLoading, refetch } = useDistanceChangeData(mid, visitdate ?? null, cid);
  const { saveMutation, isLoading: isSaving } = useDistanceSaveMutation();

  const [rows, setRows] = useState<EditableRow[]>([]);
  // Visit ids whose pin user dragged in this session — sent to BE so it can
  // persist the new lat/lon to latlon_caches (mirrors A2 latlon_visit_id flag).
  // Reset whenever the API data refreshes (handled in the same useEffect).
  const [latlonDraggedIds, setLatlonDraggedIds] = useState<Set<number>>(new Set());
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const segmentPolylinesRef = useRef<any[]>([]);

  // Sync API data → editable rows
  useEffect(() => {
    if (!data?.rows) return;
    const initialRows = data.rows.map((r, i) => ({
      ...r,
      _distance: isAutoMeasureIncorrect(r) ? '-1' : r.distance != null ? String(r.distance) : '',
      _is_non_charge: r.is_non_charge,
      _is_non_visit_treatment: r.is_non_visit_treatment,
      _is_from_massager: i === 0 ? true : r.is_distance_from_massager,
      _group_number: r.group_number != null ? String(r.group_number) : '',
      _latitude: r.to_latitude,
      _longitude: r.to_longitude,
      _edit_manual: false,
    }));
    setRows(initialRows);
    // New API snapshot → previous drags belong to the prior dataset; reset.
    setLatlonDraggedIds(new Set());
  }, [data]);

  const canEditRow = useCallback(
    (row: EditableRow) => {
      return !row.is_locked || (isAdminClinicLogin && !row.is_rezept_finalized);
    },
    [isAdminClinicLogin]
  );

  // Update 通所/訪問施術料 flag (new era)
  const handleVisitTreatmentChange = useCallback((index: number, value: boolean) => {
    setRows(prev =>
      prev.map((r, i) => (i === index ? { ...r, _is_non_visit_treatment: value } : r))
    );
  }, []);

  // Save
  const handleSave = useCallback(async () => {
    if (!mid || !visitdate) return;
    await saveMutation({
      massager_id: mid,
      visitdate,
      latlon_visit_ids: Array.from(latlonDraggedIds),
      visits: rows.map(r => ({
        id: r.id,
        distance: r._distance !== '' ? Number(r._distance) : null,
        latitude: r._latitude,
        longitude: r._longitude,
        is_non_charge: r._is_non_charge,
        is_non_visit_treatment: r._is_non_visit_treatment,
        group_number: r._group_number !== '' ? Number(r._group_number) : null,
        is_distance_from_massager: r._is_from_massager,
        edit_manual: r._edit_manual,
      })),
    });
    refetch();
  }, [mid, visitdate, rows, latlonDraggedIds, saveMutation, refetch]);

  // Close tab
  const handleClose = useCallback(() => {
    window.close();
  }, []);

  // ============ Columns ============
  const columns = useMemo<ColumnsType<EditableRow>>(() => {
    const cols: ColumnsType<EditableRow> = [
      {
        title: '番号',
        dataIndex: 'number',
        key: 'number',
        width: 60,
        align: 'center',
      },
      {
        title: '拠点',
        key: 'from',
        render: (_: unknown, row: EditableRow) => {
          const i = rows.indexOf(row);
          const startLabel = formatStartAddressLabel(row, i, rows, data?.massager);
          const nameLabel = formatStartNameLabel(row, i, rows, data?.massager);
          const isClinicLocationStart = i === 0 || row._is_from_massager;

          return (
            <div className="flex gap-1">
              {isClinicLocationStart && (
                <i className="mt-1 fa fa-building shrink-0" aria-hidden="true" />
              )}
              <div className="min-w-0 leading-5">
                <div className="truncate">{startLabel || '—'}</div>
                <div className="text-gray-500 truncate">{nameLabel || '—'}</div>
              </div>
            </div>
          );
        },
      },
      {
        title: '訪問先',
        key: 'to',
        render: (_: unknown, row: EditableRow) => {
          const destination = formatVisitDestinationAddressLabel(row);
          const patientLabel = formatPatientLabel(row);
          return (
            <div className="min-w-0 leading-5">
              <div className="truncate">{destination || '—'}</div>
              <div className="text-gray-500 truncate">{patientLabel || '—'}</div>
            </div>
          );
        },
      },
      {
        title: '距離(Km)',
        key: 'distance',
        width: 120,
        render: (_: unknown, row: EditableRow) => {
          const num = row._distance !== '' ? Number(row._distance) : NaN;
          const overLimit =
            Number.isFinite(num) && num > VISIT_DISTANCE_THRESHOLD_KM && !row._is_non_charge;
          return (
            <AppTextField
              value={row._distance}
              disabled
              size="small"
              className={
                isAutoMeasureIncorrect(row)
                  ? '!text-red-600'
                  : overLimit
                    ? '!text-red-600 line-through'
                    : ''
              }
            />
          );
        },
      },
    ];

    cols.push(
      {
        title: '通所',
        key: 'tsusho',
        width: 80,
        align: 'center',
        render: (_: unknown, row: EditableRow) => {
          const i = rows.indexOf(row);
          const forceTsusho =
            row.is_non_distance_price_visit ||
            row.is_not_transport_cost ||
            row.walking_condition_id === 2;
          return (
            <AppRadio
              name={`visit_treatment_${row.id}`}
              checked={forceTsusho || row._is_non_visit_treatment === true}
              disabled
              onChange={() => handleVisitTreatmentChange(i, true)}
            />
          );
        },
      },
      {
        title: '訪問施術料',
        key: 'visit_treatment',
        width: 100,
        align: 'center',
        render: (_: unknown, row: EditableRow) => {
          const i = rows.indexOf(row);
          const forceTsusho =
            row.is_non_distance_price_visit ||
            row.is_not_transport_cost ||
            row.walking_condition_id === 2;
          return (
            <AppRadio
              name={`visit_treatment_${row.id}`}
              checked={!forceTsusho && row._is_non_visit_treatment === false}
              disabled
              onChange={() => handleVisitTreatmentChange(i, false)}
            />
          );
        },
      }
    );

    // UAT 2026-05-04: 訪問グループ is always disabled. Group number is auto-assigned by BE
    // based on 5 conditions (same date + same clinic + same lat/lng + patient_address3_check=0
    // + walking_condition_id=2 & is_non_distance_price_visit=0). 持ち回り spec is deprecated.
    cols.push({
      title: '訪問グループ',
      key: 'group',
      width: 120,
      render: (_: unknown, row: EditableRow) => (
        <AppTextField value={row._group_number} disabled size="small" />
      ),
    });

    cols.push({
      title: 'グループ人数',
      key: 'group_count',
      width: 100,
      render: (_: unknown, row: EditableRow) => (
        <AppTextField
          value={row.group_count != null ? String(row.group_count) : ''}
          disabled
          size="small"
        />
      ),
    });

    return cols;
  }, [rows, data, handleVisitTreatmentChange]);

  // ============ Google Maps ============
  const allPoints = useMemo(() => {
    if (!data?.massager) return [];
    const points: Array<{
      lat: number;
      lng: number;
      label: string;
      infoHtml: string;
      draggable: boolean;
      rowIndex: number;
      type: 'clinic_location' | 'visit';
    }> = [];

    // Clinic location pin (display only). Falls back to legacy massager fields if
    // the API response does not include clinic_location yet.
    const location = data.clinic_location;
    const mLat = Number(location?.latitude ?? data.massager?.latitude);
    const mLng = Number(location?.longitude ?? data.massager?.longitude);
    if (!isNaN(mLat) && !isNaN(mLng) && mLat !== 0 && mLng !== 0) {
      const locationName = location?.name || data.massager?.name || '拠点';
      const locationAddress = location
        ? joinDisplayParts([location.address1, location.address2, location.address3])
        : data.massager?.address || '';
      points.push({
        lat: mLat,
        lng: mLng,
        label: locationName,
        infoHtml: `<div style="font-size:12px;line-height:1.45;white-space:pre-wrap;">${escapeHtml(locationName)}\n\n${escapeHtml(locationAddress)}</div>`,
        draggable: false,
        rowIndex: -1,
        type: 'clinic_location',
      });
    }

    // Visit pins (lat/lng may also be string from API)
    // Draggable only when DB distance < 0 (auto-measure was inaccurate) and not locked.
    // Uses original r.distance (from API), NOT r._distance (editable/recalculated),
    // so the pin stays draggable after user drags to a new position.
    rows.forEach((r, i) => {
      const rLat = Number(r._latitude);
      const rLng = Number(r._longitude);
      if (!isNaN(rLat) && !isNaN(rLng) && rLat !== 0 && rLng !== 0) {
        const canDrag = isAdminClinicLogin
          ? !r.is_rezept_finalized
          : !r.is_locked && isAutoMeasureIncorrect(r);
        points.push({
          lat: rLat,
          lng: rLng,
          label: `${r.number}: ${r.to_name || r.patient_name}`,
          infoHtml: `<div style="font-size:12px">${escapeHtml(`${r.number}: ${r.to_name || r.patient_name}`)}</div>`,
          draggable: canDrag,
          rowIndex: i,
          type: 'visit',
        });
      }
    });

    return points;
  }, [data, rows, isAdminClinicLogin]);

  // Load Google Maps script
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) return;
    if ((window as any).google?.maps) return;
    if (document.getElementById('google-maps-script')) return;

    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry`;
    script.async = true;
    document.head.appendChild(script);
  }, []);

  // Initialize / update map
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY || !mapRef.current) return;

    const initMap = () => {
      const gm = (window as any).google?.maps;
      if (!gm) return;

      // Clean up old markers
      markersRef.current.forEach((m: any) => m.setMap(null));
      markersRef.current = [];
      segmentPolylinesRef.current.forEach((p: any) => p.setMap(null));
      segmentPolylinesRef.current = [];

      const center = allPoints[0];
      if (!googleMapRef.current) {
        if (!center) return;
        googleMapRef.current = new gm.Map(mapRef.current!, {
          center: { lat: center.lat, lng: center.lng },
          zoom: 14,
        });
      }
      const map = googleMapRef.current;

      const bounds = new gm.LatLngBounds();

      allPoints.forEach(pt => {
        const position = { lat: pt.lat, lng: pt.lng };
        bounds.extend(position);

        const pinColor = pt.type === 'clinic_location' ? 'green' : pt.draggable ? 'blue' : 'red';
        const iconUrl = `https://maps.google.com/mapfiles/ms/icons/${pinColor}-dot.png`;

        const marker = new gm.Marker({
          map,
          position,
          draggable: pt.draggable,
          title: pt.label,
          label: {
            text: String(pt.type === 'clinic_location' ? '拠' : pt.rowIndex + 1),
            color: 'white',
            fontSize: '11px',
          },
          icon: {
            url: iconUrl,
            scaledSize: new gm.Size(40, 40),
          },
        });

        // Info window on click
        const infoWindow = new gm.InfoWindow({ content: pt.infoHtml });
        marker.addListener('click', () => infoWindow.open(map, marker));

        if (pt.draggable && pt.rowIndex >= 0) {
          marker.addListener('dragend', () => {
            const pos = marker.getPosition();
            if (!pos) return;
            const newLat = pos.lat();
            const newLng = pos.lng();

            const draggedVisitId = rows[pt.rowIndex]?.id;
            if (typeof draggedVisitId === 'number') {
              setLatlonDraggedIds(prev => {
                if (prev.has(draggedVisitId)) return prev;
                const next = new Set(prev);
                next.add(draggedVisitId);
                return next;
              });
            }

            setRows(prev => {
              const draft = prev.map((r, i) =>
                i === pt.rowIndex
                  ? { ...r, _latitude: newLat, _longitude: newLng, _edit_manual: true }
                  : r
              );
              // Cascade: moving 訪問先 k updates row k+1.. when 拠点 = 前訪問先 (from = prev row).
              // Per confirm #3 (drag pin = manual edit), mark _edit_manual=true on the
              // dragged row AND all downstream rows whose distance recomputed.
              const recalc = recalculateDistancesFromRowIndex(draft, pt.rowIndex, data?.massager);
              return recalc.map((r, i) =>
                i >= pt.rowIndex && canRecalculateDistance(r) ? { ...r, _edit_manual: true } : r
              );
            });
          });
        }

        markersRef.current.push(marker);
      });

      // Red segment per visit row (起点 respects automatic 拠点: 施術者 or 前の訪問先)
      rows.forEach((row, rowIdx) => {
        const fromPt = resolveDistanceFromPoint(rowIdx, row, rows, data?.massager);
        const toPt = resolveToPoint(row);
        if (fromPt && toPt) {
          const pl = new gm.Polyline({
            path: [fromPt, toPt],
            strokeColor: '#FF0000',
            strokeWeight: 2,
            map,
          });
          segmentPolylinesRef.current.push(pl);
          bounds.extend(fromPt);
          bounds.extend(toPt);
        }
      });

      if (allPoints.length > 1) {
        map.fitBounds(bounds, 60);
      }
    };

    // Wait for google maps to load
    const gm = (window as any).google?.maps;
    if (gm) {
      initMap();
    } else {
      const interval = setInterval(() => {
        if ((window as any).google?.maps) {
          clearInterval(interval);
          initMap();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [allPoints, data, rows]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <AppSpin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-4">
        <AppTypography.H5>距離編集</AppTypography.H5>
        <AppCard className="p-6 mt-4 text-center text-gray-500">データがありません。</AppCard>
      </div>
    );
  }

  return (
    <div className="p-2 md:p-4">
      <AppTypography.H5 className="mb-2">距離編集</AppTypography.H5>

      <AppCard className="p-3">
        <div className="mb-2 text-red-600">
          ※ 距離が「-1」の場合、地図上の訪問先受診者のピンを移動し正しい建物を特定してください
        </div>

        {/* Data table */}
        <AppTable<EditableRow>
          dataSource={rows}
          rowKey="id"
          rowClassName={row =>
            `${row.is_error ? '!bg-[rgb(255,240,245)]' : ''} ${row.is_locked ? '!bg-gray-100' : ''}`
          }
          columns={columns}
        />

        {/* Google Maps */}
        {GOOGLE_MAPS_API_KEY ? (
          <div
            ref={mapRef}
            className="w-full mt-3 border border-gray-200 rounded distance-change-map"
            style={{ height: 500 }}
          />
        ) : (
          <div className="p-6 mt-3 text-sm text-center text-gray-500 border border-gray-200 rounded bg-gray-50">
            Google Maps API Keyが設定されていません。
            <br />
            .envファイルにVITE_GOOGLE_MAPS_API_KEYを設定してください。
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-4">
          {rows.some(canEditRow) && (
            <AppButton type="primary" onClick={handleSave} loading={isSaving}>
              更新
            </AppButton>
          )}
          <AppButton onClick={handleClose}>閉じる</AppButton>
        </div>
      </AppCard>
    </div>
  );
}
